import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { isStaffRegulatorCreateAborted } from "./httpErrors";
import { parseStaffRegulator } from "./parseStaffRegulator";
import { STAFF_REGULATORS_LIST_QUERY_KEY } from "./useStaffRegulatorsList";
import { waitForCreatedStaffRegulator } from "./waitForCreatedStaffRegulator";
import type {
  CreateStaffRegulatorRequest,
  StaffRegulator,
  StaffRegulatorEnvelope,
} from "./types";

/** Client budget for POST + list reconcile after a gateway abort. */
export const CREATE_STAFF_REGULATOR_TIMEOUT_MS = 29_000;

/** Time reserved for list GET polling after the POST is aborted. */
export const CREATE_STAFF_REGULATOR_POLL_BUDGET_MS = 5_000;

export const CREATE_STAFF_REGULATOR_POST_TIMEOUT_MS =
  CREATE_STAFF_REGULATOR_TIMEOUT_MS - CREATE_STAFF_REGULATOR_POLL_BUDGET_MS;

export const postCreateStaffRegulator = async (
  payload: CreateStaffRegulatorRequest,
): Promise<StaffRegulator> => {
  const deadlineMs = Date.now() + CREATE_STAFF_REGULATOR_TIMEOUT_MS;
  try {
    const response = await getPrivateInstance().post<
      StaffRegulatorEnvelope<unknown>
    >(API_ROUTES.STAFF_REGULATORS, payload, {
      timeout: CREATE_STAFF_REGULATOR_POST_TIMEOUT_MS,
    });
    const parsed = parseStaffRegulator(response.data?.data);
    if (!parsed) {
      throw new Error("staff-regulators.invalid-create");
    }
    return parsed;
  } catch (error) {
    if (!isStaffRegulatorCreateAborted(error)) {
      throw error;
    }
    const created = await waitForCreatedStaffRegulator(
      payload.email,
      deadlineMs,
    );
    if (created) {
      return created;
    }
    throw error;
  }
};

export const useCreateStaffRegulator = (): UseMutationResult<
  StaffRegulator,
  Error,
  CreateStaffRegulatorRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["staff-regulators", "create"],
    mutationFn: postCreateStaffRegulator,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STAFF_REGULATORS_LIST_QUERY_KEY,
      });
    },
  });
};
